import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Printer, FileText, Building2, Download, Eye, Briefcase, Loader2, Sticker } from "lucide-react";
import { Link } from "react-router-dom";
import { PdfViewerModal } from "../components/PdfViewerModal";

export default function MenuCards() {
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Endpoints publics (shop) pour éviter les soucis CORS cross-origin admin → backend
        const [cat, dists] = await Promise.all([
          api.get("/shop/menu-cards/catalog"),
          api.get("/distributors"),
        ]);
        setCatalogProducts(cat.data.products || []);
        setDistributors((dists.data || []).filter((d) => d.type === "AUTOMATIQUE"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const [distContents, setDistContents] = useState({}); // { distId: products[] } pour libellés "X affichés"

  const loadDistributorContent = async (distId) => {
    if (distContents[distId]) return;
    try {
      const r = await api.get(`/shop/distributors/${distId}/menu-card`);
      setDistContents((s) => ({ ...s, [distId]: r.data.products || [] }));
    } catch {
      setDistContents((s) => ({ ...s, [distId]: [] }));
    }
  };

  useEffect(() => {
    distributors.forEach((d) => loadDistributorContent(d.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributors]);

  // Plan Z — PDF généré côté serveur (photos JPEG embarquées, contourne tous les soucis cross-origin / WebP).
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || (typeof window !== "undefined" ? window.location.origin : "");

  // State pour le PDF viewer modal intégré (contourne les viewers natifs Brave/Firefox qui forcent download)
  const [pdfViewer, setPdfViewer] = useState({ open: false, blob: null, filename: "", title: "", loading: false });

  // Helper : récupère un PDF du backend en blob.
  const fetchPdfBlob = async (url, opts = {}) => {
    const r = await fetch(url, { credentials: "include", ...opts });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.blob();
  };

  // Helper unifié : ouvre le modal viewer (preview) ou télécharge (download).
  const openOrDownload = (blob, action, filename, title) => {
    if (action === "preview") {
      // Ouvre le modal viewer intégré → garantit l'affichage inline indépendamment du navigateur.
      setPdfViewer({ open: true, blob, filename, title: title || "Aperçu PDF", loading: false });
    } else {
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    }
  };

  const onCatalog = async (action) => {
    try {
      const blob = await fetchPdfBlob(`${apiBase}/api/shop/menu-cards/catalog.pdf`);
      openOrDownload(blob, action, `catalogue-barquettes-${new Date().toISOString().slice(0, 10)}.pdf`, "Catalogue complet — Maison Thaï");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Erreur génération PDF catalogue : " + e.message);
    }
  };

  const onDistributor = async (d, action) => {
    try {
      // Cache-buster `t` pour garantir un rendu frais après modif des slots
      const blob = await fetchPdfBlob(`${apiBase}/api/shop/distributors/${d.id}/menu-card.pdf?t=${Date.now()}`);
      const safeName = (d.name || "distributeur").replace(/[^a-z0-9]/gi, "_");
      openOrDownload(blob, action, `fiche-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`, `Fiche ${d.name}`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Erreur génération PDF distributeur : " + e.message);
    }
  };

  const onDistributorCompact = async (d, action) => {
    try {
      const blob = await fetchPdfBlob(`${apiBase}/api/shop/distributors/${d.id}/menu-card-compact.pdf?t=${Date.now()}`);
      const safeName = (d.name || "distributeur").replace(/[^a-z0-9]/gi, "_");
      openOrDownload(blob, action, `fiche-vitrine-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`, `Fiche vitrine ${d.name}`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Erreur génération PDF vitrine : " + e.message);
    }
  };

  // ============== Tarifs revendeurs : menu déroulant + génération PDF nominatif ==============
  const [resellers, setResellers] = useState([]);
  const [selectedResellerId, setSelectedResellerId] = useState("");
  const [resellerGenerating, setResellerGenerating] = useState(false);

  useEffect(() => {
    api.get("/resellers").then((r) => setResellers(r.data || [])).catch(() => {});
  }, []);

  const selectedReseller = useMemo(
    () => resellers.find((r) => r.id === selectedResellerId) || null,
    [resellers, selectedResellerId]
  );

  const onResellerPdf = async (action) => {
    setResellerGenerating(true);
    try {
      const body = selectedResellerId ? { reseller_id: selectedResellerId } : {};
      const blob = await fetchPdfBlob(`${apiBase}/api/price-lists/reseller.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const slug = selectedReseller
        ? (selectedReseller.name || "revendeur").replace(/[^a-z0-9]/gi, "_")
        : "generique";
      const title = selectedReseller ? `Tarifs revendeur · ${selectedReseller.name}` : "Tarifs revendeurs — Catalogue générique";
      openOrDownload(blob, action, `tarifs-revendeurs-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`, title);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Erreur génération PDF tarifs revendeurs : " + e.message);
    } finally {
      setResellerGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="menu-cards-page">
      <header>
        <h2 className="text-xl font-heading flex items-center gap-2">
          <Printer className="w-5 h-5 text-[#8C6B2F]" /> Cartes imprimables — Barquettes
        </h2>
        <p className="text-sm text-[#575A57] mt-1">
          Générez des fiches A4 (format paysage 3 colonnes, palette parchemin sans noir, économe en encre) pour vos distributeurs et revendeurs.
        </p>
      </header>

      {/* Phase 98q — Menu Tuiles digital (QR code sur les machines) */}
      <section className="bg-[#0F0F0F] text-[#F2ECE4] border border-[#1F1F1F] rounded-lg p-5" data-testid="menu-tuiles-cta">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-[#C1A063]/15 flex items-center justify-center flex-shrink-0">
              <Sticker className="w-5 h-5 text-[#C1A063]" />
            </div>
            <div>
              <h3 className="font-heading text-lg">Menu Tuiles digital <span className="text-[10px] uppercase tracking-widest text-[#C1A063] ml-2">nouveau</span></h3>
              <p className="text-sm text-[#8B8B85] mt-1">
                Page publique mobile-first en mosaïque, consultable via QR code posé à côté d'une machine. Alternative moderne au PDF.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 font-mono text-[11px]">
                <a
                  href="/carte"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#C1A063] underline hover:no-underline"
                  data-testid="menu-tuiles-preview-global"
                >
                  /carte (catalogue global) ↗
                </a>
                <span className="text-[#575A57]">·</span>
                <a
                  href="#per-machine-links"
                  onClick={(e) => { e.preventDefault(); document.getElementById("per-machine-links")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="text-[#C1A063] underline hover:no-underline"
                >
                  Lien par machine ↓
                </a>
              </div>
            </div>
          </div>
          <Link
            to="/carte"
            target="_blank"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#C1A063] text-[#C1A063] hover:bg-[#C1A063]/10 text-xs uppercase tracking-[0.18em] rounded"
            data-testid="menu-tuiles-open"
          >
            <Eye className="w-4 h-4" /> Voir la carte
          </Link>
        </div>
      </section>

      {/* Catalogue complet */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <FileText className="w-6 h-6 text-[#8C6B2F] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-heading text-lg">Catalogue complet des barquettes</h3>
              <p className="text-sm text-[#575A57] mt-1">
                Toutes les barquettes vendables (avec photo + prix internet). À remettre aux revendeurs et partenaires.
              </p>
              <p className="text-xs text-[#A3A6A3] mt-2">
                {loading ? "Chargement…" : `${catalogProducts.length} produits visibles`}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => onCatalog("preview")} disabled={loading || catalogProducts.length === 0} data-testid="preview-catalog">
              <Eye className="w-4 h-4 mr-2" /> Aperçu
            </Button>
            <Button onClick={() => onCatalog("download")} disabled={loading || catalogProducts.length === 0} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="download-catalog">
              <Download className="w-4 h-4 mr-2" /> Télécharger PDF
            </Button>
          </div>
        </div>
        {/* Bande de miniatures retirée — on garde la liste propre */}
      </section>

      {/* Tarifs revendeurs */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="reseller-section">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-start gap-3">
            <Briefcase className="w-6 h-6 text-[#8C6B2F] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-heading text-lg">Tarifs revendeurs</h3>
              <p className="text-sm text-[#575A57] mt-1 max-w-2xl">
                PDF imprimable avec photo, SKU, prix revendeur (B2B), prix conseillé (boutique en ligne) et marge calculée.
                Choisissez un revendeur dans la liste pour un PDF nominatif avec ses prix override, ou laissez vide pour le catalogue générique.
                Les revendeurs se gèrent dans <Link to="/revendeurs" className="text-[#1F4E3D] hover:underline">Revendeurs</Link>.
              </p>
            </div>
          </div>
        </div>

        {/* Sélecteur revendeur */}
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-[280px]">
            <Select value={selectedResellerId || "__generic__"} onValueChange={(v) => setSelectedResellerId(v === "__generic__" ? "" : v)}>
              <SelectTrigger data-testid="reseller-select">
                <SelectValue placeholder="Choisir un revendeur…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__generic__">
                  <span className="italic">Catalogue générique (tous les plats avec prix B2B)</span>
                </SelectItem>
                {resellers.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`reseller-option-${r.id}`}>
                    {r.name}{r.city ? ` · ${r.city}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReseller && (
              <p className="text-xs text-[#A3A6A3] mt-2">
                {selectedReseller.displayed_product_ids?.length > 0
                  ? <>📋 Catalogue : <strong className="text-[#8C6B2F]">{selectedReseller.displayed_product_ids.length}</strong> plat{selectedReseller.displayed_product_ids.length > 1 ? "s" : ""} sélectionné{selectedReseller.displayed_product_ids.length > 1 ? "s" : ""}</>
                  : <span className="italic">📋 Catalogue : tous les plats éligibles</span>}
                {" · "}
                {Object.keys(selectedReseller.price_overrides || {}).length > 0
                  ? <>💰 <strong className="text-[#8C6B2F]">{Object.keys(selectedReseller.price_overrides || {}).length}</strong> prix personnalisé{Object.keys(selectedReseller.price_overrides || {}).length > 1 ? "s" : ""}</>
                  : <span className="italic">💰 Prix standard B2B</span>}
              </p>
            )}
            {resellers.length === 0 && (
              <p className="text-xs text-[#A3A6A3] mt-2 italic">
                Aucun revendeur enregistré. <Link to="/revendeurs" className="text-[#1F4E3D] hover:underline">Créer mon premier revendeur →</Link>
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => onResellerPdf("preview")} disabled={resellerGenerating} data-testid="preview-reseller">
              {resellerGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Aperçu
            </Button>
            <Button onClick={() => onResellerPdf("download")} disabled={resellerGenerating} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="download-reseller">
              <Download className="w-4 h-4 mr-2" /> Télécharger PDF
            </Button>
          </div>
        </div>

        <p className="text-[11px] text-[#A3A6A3]">
          Format PDF : <span className="font-mono"><span className="text-[#8C6B2F]">Prix revendeur</span> / <span className="text-[#1F4E3D]">Prix conseillé</span> / <span className="text-[#8C6B2F]">Marge %</span></span>. Coordonnées Maison Thaï + conditions commerciales nominatives (notes du revendeur) ou génériques (depuis <Link to="/parametres/cms-boutique" className="text-[#1F4E3D] hover:underline">CMS boutique</Link>) en en-tête.
        </p>
      </section>

      {/* Par distributeur */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <div className="flex items-start gap-3 mb-4">
          <Building2 className="w-6 h-6 text-[#8C6B2F] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading text-lg">Fiches par distributeur</h3>
            <p className="text-sm text-[#575A57] mt-1">
              Une fiche A4 par machine, listant uniquement les plats qui s'y trouvent. À coller sur la porte ou à laisser en évidence.
              Sélectionnez les produits dans la fiche du distributeur :
              <Link to="/distributeurs" className="text-[#1F4E3D] hover:underline ml-1">Gérer les distributeurs →</Link>
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {distributors.map((d) => {
            const count = (d.displayed_product_ids || []).length;
            const prods = distContents[d.id];
            return (
              <div key={d.id} className="border border-[#E5E5E0] rounded p-3 flex items-center justify-between gap-3 hover:bg-[#FBFAF6]" data-testid={`dist-card-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.name}</div>
                  <div className="text-xs text-[#A3A6A3] mt-0.5">
                    {count > 0 ? `${count} produit${count > 1 ? "s" : ""} sélectionné${count > 1 ? "s" : ""}` : "Auto (lots actifs)"}
                    {prods && ` · ${prods.length} affiché${prods.length > 1 ? "s" : ""}`}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => onDistributor(d, "preview")} data-testid={`preview-dist-${d.id}`} title="Aperçu fiche partenaire (multi-pages)">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onDistributor(d, "download")} data-testid={`download-dist-${d.id}`} title="Télécharger fiche partenaire">
                    <Download className="w-4 h-4" />
                  </Button>
                  {/* Phase 98 fix — Aperçu vitrine avant impression (bug user : bouton unique téléchargeait sans preview) */}
                  <Button variant="ghost" size="sm" onClick={() => onDistributorCompact(d, "preview")} data-testid={`preview-dist-compact-${d.id}`} title="Aperçu fiche vitrine compacte (1 page)">
                    <Sticker className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onDistributorCompact(d, "download")} data-testid={`download-dist-compact-${d.id}`} title="Télécharger fiche vitrine compacte (1 page)" className="border-[#1F4E3D] text-[#1F4E3D]">
                    <Download className="w-4 h-4" />
                  </Button>
                  {/* Phase 98q — Lien Menu Tuiles digital (à mettre en QR code sur la machine) */}
                  <a
                    href={`/carte/${d.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Ouvrir la carte digitale (tuiles) — URL à mettre en QR code sur la machine"
                    className="inline-flex items-center justify-center w-9 h-9 border border-[#C1A063] text-[#C1A063] hover:bg-[#C1A063]/10 rounded-md text-xs uppercase tracking-wider"
                    data-testid={`menu-tuiles-dist-${d.id}`}
                  >
                    QR
                  </a>
                </div>
              </div>
            );
          })}
        </div>
        <div id="per-machine-links" />
      </section>

      {/* Aide */}
      <p className="text-xs text-[#A3A6A3] text-center pt-2">
        Astuce — l'aperçu s'affiche dans un viewer intégré (indépendant du navigateur et des extensions).
      </p>

      {/* PDF Viewer Modal */}
      <PdfViewerModal
        open={pdfViewer.open}
        onClose={() => setPdfViewer({ ...pdfViewer, open: false })}
        blob={pdfViewer.blob}
        filename={pdfViewer.filename}
        title={pdfViewer.title}
        loading={pdfViewer.loading}
      />
    </div>
  );
}
